import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/layout/PublicNav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { await refreshUser(); setStatus("success"); return; }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current > 10) { setStatus("error"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts.current > 10) setStatus("error"); else timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [refreshUser]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5 grain">
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="payment-status">
        <Logo />
        <div className="mt-8">
          {status === "checking" && (<><Loader2 className="mx-auto h-12 w-12 animate-spin text-emerald" /><h1 className="mt-5 font-heading text-2xl font-bold text-navy">Confirming your payment…</h1><p className="mt-2 text-slate-600">This only takes a moment.</p></>)}
          {status === "success" && (<><CheckCircle2 className="mx-auto h-12 w-12 text-emerald" /><h1 className="mt-5 font-heading text-2xl font-bold text-navy">Membership activated!</h1><p className="mt-2 text-slate-600">Your new plan is live. Time to dive in.</p><Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="payment-dashboard-btn">Go to dashboard</Button></>)}
          {status === "error" && (<><XCircle className="mx-auto h-12 w-12 text-red-500" /><h1 className="mt-5 font-heading text-2xl font-bold text-navy">Payment not confirmed</h1><p className="mt-2 text-slate-600">If you completed payment, it may take a moment. Otherwise, please try again.</p><Button onClick={() => navigate("/pricing")} className="mt-6 rounded-md bg-navy text-white" data-testid="payment-retry-btn">Back to pricing</Button></>)}
        </div>
      </div>
    </div>
  );
}
